<template>
  <div class="bottom-overlay__wrapper" :class="{ isActive }">
    <div
      class="p-4 bg-white/80 dark:bg-black/80 border-t border-neutral-200 dark:border-neutral-600 bottom-overlay__inner"
      style="backdrop-filter: blur(16px)"
    >
      <NamiSliderNew
        v-if="sourceReadingDirection !== 'vls'"
        :model-value="currChProgress"
        @update:modelValue="updateCurrentIndex"
        :min="1"
        :max="currChPageNums.length"
        :reverse="isRtl"
        :loading="loading"
        :no-labels="loading"
        noBg
        hide-number
        immediate
        :flag="flagIndex"
        :highlight="
          flagIndex
            ? {
                color: 'danger',
                min: flagIndex.index - 1,
              }
            : undefined
        "
        @dragStart="emit('dragStart')"
        @dragEnd="emit('dragEnd')"
      >
        <template #label-prepend="{ modelValue }">
          <div class="min-w-[1rem] text-center">
            {{ currChPageNums[modelValue - 1] }}
          </div>
        </template>
        <template #label-postpend>
          <div class="min-w-[1rem] text-center">
            {{ currChPageNums[currChPageNums.length - 1] }}
          </div>
        </template>
        <template #label-tooltip="{ modelValue }">
          <div class="min-w-[1rem] text-center">
            {{ currChPageNums[modelValue - 1] }}
          </div>
        </template>
      </NamiSliderNew>
      <NamiSliderNew
        v-else
        :model-value="lsProgress * 100"
        @update:modelValue="(v) => emit('update:lsProgress', v / 100)"
        :min="0"
        :max="100"
        :loading="loading"
        noBg
        hide-number
        continuous
        @dragStart="emit('dragStart')"
        @dragEnd="emit('dragEnd')"
      >
        <template #label-prepend="{ modelValue }">
          <div class="min-w-[1rem] text-center">
            {{ modelValue.toFixed(0) }}%
          </div>
        </template>
        <template #label-tooltip="{ modelValue }">
          <div class="min-w-[1rem] text-center">
            {{ modelValue.toFixed(2) }}%
          </div>
        </template>
        <template #label-postpend="{ max }">
          <div class="min-w-[1rem] text-center">{{ max }}%</div>
        </template>
      </NamiSliderNew>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useReaderSettings } from "~/stores/readerSettings";
import type { UnwrapNestedRefs } from "vue";
import { ReaderManager } from "~/src/reader/managers/reader";
import { isContentPageGroup, isImagePageGroup } from "~/src/reader/utils";
import {
  type ImagePageGroup,
  SubscribeLockedPageGroup,
} from "~/src/reader/managers/pageGroup";
import type { TitleReadingMode } from "~/src/api";

interface Props {
  sourceReadingDirection: TitleReadingMode;
  readerManager: UnwrapNestedRefs<ReaderManager>;
  flag?: {
    new: boolean;
    chapterId: string;
    sourceUrl: string;
    highUrl: string;
    mediumUrl: string;
    lowUrl: string;
  };
  isActive?: boolean;
  loading?: boolean;
  lsProgress: number;
}

interface Events {
  (e: "update:lsProgress", v: number): void;
  (e: "dragStart"): void;
  (e: "dragEnd"): void;
}

const { readerManager, flag, sourceReadingDirection } = defineProps<Props>();
const emit = defineEmits<Events>();
const { readingDirection } = storeToRefs(useReaderSettings());

const flagIndex = computed(() => {
  if (!flag) return undefined;
  if (!flag.new) return undefined;
  if (flag.chapterId !== currentChapterId.value) return undefined;
  const { highUrl, sourceUrl, lowUrl, mediumUrl } = flag;
  let url = sourceUrl;
  switch (readerManager.quality) {
    case "high":
      url = highUrl;
      break;
    case "medium":
      url = mediumUrl;
      break;
    case "low":
      url = lowUrl;
      break;
    default:
      url = sourceUrl;
  }

  return {
    text: "New",
    direction: "small-to-large" as "small-to-large",
    index: Math.max(
      0,
      readerManager.pageArray
        .filter(isImagePageGroup)
        .findIndex((page) => page.pages.some((p) => p.serverSrc === url)) + 1,
    ),
  };
});

const currentChapterId = computed(() => readerManager.getCurrChapterId());

const currChPageNums = computed(() => {
  if (!currentChapterId.value) return [];
  const chapterPages = readerManager.pageArray.filter(
    (page) =>
      page.chapterId === currentChapterId.value && isContentPageGroup(page),
  ) as (ImagePageGroup | SubscribeLockedPageGroup)[];
  let count = 0;
  return chapterPages.map((page) => {
    if (isImagePageGroup(page)) {
      count += 1;
      const range = [count, count + page.pages.length - 1];
      count = range[1];
      return range[0] === range[1] ? range[0].toString() : range.join("-");
    }

    return "Locked";
  });
});

const currChProgress = computed(() => {
  if (!currentChapterId.value) return 1;
  const firstIndex = readerManager.pageArray.findIndex(
    (page) => page.chapterId === currentChapterId.value,
  );
  const normalizedPageIndex = Math.max(
    0,
    readerManager.currentPageIndex - firstIndex,
  );

  if (sourceReadingDirection === "vls")
    return normalizedPageIndex / Math.max(currChPageNums.value.length, 1);
  else return Math.min(normalizedPageIndex + 1, currChPageNums.value.length);
});

const isRtl = computed(() => {
  if (readingDirection.value === "content") {
    return sourceReadingDirection === "rtl";
  } else {
    return readingDirection.value === "rtl";
  }
});

const updateCurrentIndex = (v: number) => {
  if (
    !currentChapterId.value ||
    (readerManager.currentPageIndex === 0 &&
      !isContentPageGroup(readerManager.pageArray[0]))
  )
    return;
  const firstIndex = readerManager.pageArray.findIndex(
    (page) =>
      page.chapterId === currentChapterId.value && isContentPageGroup(page),
  );

  readerManager.currentPageIndex = Math.floor(firstIndex + v - 1);
};
</script>

<style scoped lang="postcss">
.bottom-overlay__wrapper {
  & > .bottom-overlay__inner {
    transform: translateY(100%);
    transition: transform 150ms ease-in-out;
  }

  &:hover:not(.isZoomed) > .bottom-overlay__inner {
    @apply canhover:transform-none;
  }

  &.isActive:not(.isZoomed) > .bottom-overlay__inner {
    transform: none;
  }
}
</style>
